import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import {
  areaLabels,
  creditAuthority,
  decisionLabels,
  eventLabels,
  impedimentLabels,
  type AuthorityStanding,
  type Impediment,
  type ImpedimentCode,
  type OperationAuthority,
} from './api';

/**
 * Pareceres, alcadas e bloqueios da operacao (C-ALC), para a tela da operacao e o
 * cartao da esteira.
 *
 * Mostra o que o servidor calculou: os impedimentos de emissao com o motivo e a fonte,
 * a liberacao de quem tem a permissao, a alcada de quem le para o valor desta
 * operacao, os encaminhamentos ao nivel acima, o historico das decisoes e o resultado
 * da ultima execucao do motor. A tela nao decide nada: quem confere alcada e
 * impedimento e o servidor, que repete tudo a cada pedido.
 */
export function OperationAuthorityPanel({
  operationId,
  compact = false,
}: {
  operationId: string;
  /** No cartao da esteira, sem o historico completo. */
  compact?: boolean;
}) {
  const { can, session } = useAuth();
  // Alcada e impedimento sao internos da seguradora: a corretora nao ve (o servidor
  // responde 403 para o acesso com escopo de corretora).
  const podeVer =
    !session?.broker_scoped &&
    (can('emissao.bloqueio.view') ||
      can('credito.analise.view') ||
      can('subscricao.analise.view') ||
      can('alcadas.matriz.view'));

  const consulta = useQuery({
    queryKey: ['credit-authority', operationId],
    queryFn: ({ signal }) => creditAuthority.operation(operationId, signal),
    enabled: podeVer && operationId !== '',
  });

  if (!podeVer) return null;

  if (consulta.isPending) {
    return (
      <Card>
        <CardBody>
          <SkeletonText lines={4} />
        </CardBody>
      </Card>
    );
  }
  if (consulta.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar alcadas e impedimentos">
        {consulta.error.message}
      </Alert>
    );
  }

  const quadro = consulta.data;
  return (
    <div className="flex flex-col gap-5">
      <ImpedimentsCard operationId={operationId} quadro={quadro} />
      <AuthorityCard quadro={quadro} />
      {quadro.engine ? <EngineCard quadro={quadro} /> : null}
      {compact ? null : <HistoryCard quadro={quadro} />}
    </div>
  );
}

function ImpedimentsCard({
  operationId,
  quadro,
}: {
  operationId: string;
  quadro: OperationAuthority;
}) {
  const barrando = quadro.impediments.filter((i) => i.blocks).length;
  return (
    <Card>
      <CardHeader
        title="Impedimentos de emissao"
        description="Bloqueios de credito ligados pela empresa. A emissao so acontece sem nenhum barrando."
        action={
          barrando > 0 ? (
            <Badge tone="danger">{barrando} barrando</Badge>
          ) : (
            <Badge tone="success">Nada impede</Badge>
          )
        }
      />
      <CardBody className="flex flex-col gap-3">
        {!quadro.gate_applies ? (
          <p className="text-sm text-ink-600">
            Endosso sem aumento de importancia segurada: nao passa pelo portao de
            credito.
          </p>
        ) : null}
        {quadro.impediments.length === 0 ? (
          <p className="text-sm text-ink-600">
            Nenhum impedimento de credito para esta operacao. A emissao continua com as
            conferencias de sempre (etapa, emissor, faixa de taxa e documentos).
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
            {quadro.impediments.map((imp) => (
              <ImpedimentItem
                key={imp.code}
                operationId={operationId}
                impediment={imp}
              />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ImpedimentItem({
  operationId,
  impediment,
}: {
  operationId: string;
  impediment: Impediment;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [abrir, setAbrir] = useState(false);
  const [justificativa, setJustificativa] = useState('');

  const liberar = useMutation({
    mutationFn: () =>
      creditAuthority.release(operationId, impediment.code, justificativa),
    onSuccess: () => {
      toast.success('Impedimento liberado.');
      setAbrir(false);
      setJustificativa('');
      void queryClient.invalidateQueries({
        queryKey: ['credit-authority', operationId],
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const rotulo = rotuloDoImpedimento(impediment.code);
  const fonte =
    typeof impediment.details.fonte === 'string' ? impediment.details.fonte : '';
  const podeLiberar =
    impediment.blocks && impediment.releasable && can('emissao.bloqueio.approve');

  return (
    <li className="flex flex-col gap-2 px-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink-900">{rotulo}</span>
        {impediment.release ? (
          <Badge tone="info">Liberado</Badge>
        ) : impediment.informational ? (
          <Badge tone="neutral">Informativo</Badge>
        ) : (
          <Badge tone="danger">Barra a emissao</Badge>
        )}
      </div>
      <p className="text-sm text-ink-700">{impediment.message}</p>
      {fonte ? <p className="text-2xs text-ink-500">Fonte: {fonte}</p> : null}
      {impediment.release ? (
        <p className="text-2xs text-ink-500">
          Liberado por {impediment.release.released_label} em{' '}
          {formatDateTime(impediment.release.created_at)}:{' '}
          {impediment.release.justification}
        </p>
      ) : null}
      {podeLiberar && !abrir ? (
        <div>
          <Button size="sm" variant="secondary" onClick={() => setAbrir(true)}>
            Liberar impedimento
          </Button>
        </div>
      ) : null}
      {podeLiberar && abrir ? (
        <div className="flex flex-col gap-2">
          <Textarea
            label={`Justificativa da liberacao (${rotulo})`}
            value={justificativa}
            hint="Obrigatoria. A liberacao vale para a situacao atual, passa pela sua alcada e fica na trilha."
            onChange={(event) => setJustificativa(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setAbrir(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              loading={liberar.isPending}
              disabled={justificativa.trim() === ''}
              onClick={() => liberar.mutate()}
            >
              Confirmar liberacao
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/** Rotulo do impedimento; a documentacao obrigatoria e a regra da plataforma. */
function rotuloDoImpedimento(code: string): string {
  if (code in impedimentLabels) return impedimentLabels[code as ImpedimentCode];
  if (code === 'documentacao_obrigatoria_ausente')
    return 'Documentacao obrigatoria ausente';
  return code;
}

function AuthorityCard({ quadro }: { quadro: OperationAuthority }) {
  const pendentes = quadro.referrals.filter((r) => r.status === 'pendente');
  return (
    <Card>
      <CardHeader
        title="Sua alcada nesta operacao"
        description="A alcada por valor e conferida pelo servidor em cada aprovacao e liberacao."
      />
      <CardBody className="flex flex-col gap-3">
        <ul className="flex flex-col gap-2">
          {quadro.my_authority.map((s) => (
            <StandingLine key={s.decision_type} standing={s} />
          ))}
        </ul>
        {pendentes.length > 0 ? (
          <Alert tone="warning" title="Decisao encaminhada ao nivel acima">
            {pendentes.map((r) => (
              <span key={r.id} className="block">
                {decisionLabels[r.decision_type]}: {formatMoney(r.value)}, pedido por{' '}
                {r.requested_label}
                {r.required_label
                  ? `, para ${r.required_label}`
                  : ', sem nivel que cubra o valor'}
                .
              </span>
            ))}
          </Alert>
        ) : null}
      </CardBody>
    </Card>
  );
}

function StandingLine({ standing }: { standing: AuthorityStanding }) {
  const titulo = decisionLabels[standing.decision_type];
  if (!standing.applied) {
    return (
      <li className="text-sm text-ink-700">
        <span className="font-medium">{titulo}:</span> sem alcada por valor configurada;
        vale a permissao.
      </li>
    );
  }
  const nivel = standing.level
    ? `${standing.level.label} (nivel ${standing.level.level}, ${standing.level.ceiling ? `teto ${formatMoney(standing.level.ceiling)}` : 'sem teto'})`
    : 'nenhum nivel';
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm text-ink-700">
      <span className="font-medium">{titulo}:</span>
      <span>{nivel}</span>
      {standing.value ? (
        <span>Valor desta operacao: {formatMoney(standing.value)}.</span>
      ) : null}
      {standing.allowed === true ? (
        <Badge tone="success">Dentro da sua alcada</Badge>
      ) : null}
      {standing.allowed === false ? (
        <Badge tone="warning">
          Acima da sua alcada
          {standing.required_level ? `: decide ${standing.required_level.label}` : ''}
        </Badge>
      ) : null}
      {standing.pep_minimum_level ? (
        <Badge tone="info">PEP: nivel minimo {standing.pep_minimum_level}</Badge>
      ) : null}
    </li>
  );
}

function EngineCard({ quadro }: { quadro: OperationAuthority }) {
  const motor = quadro.engine;
  if (!motor) return null;
  return (
    <Card>
      <CardHeader
        title="Ultima execucao do motor"
        description="O motor registra e recomenda; a decisao continua no parecer."
        action={
          <Badge tone={motor.mode === 'decisao' ? 'brand' : 'neutral'}>
            {motor.mode}
          </Badge>
        }
      />
      <CardBody className="flex flex-col gap-2 text-sm text-ink-700">
        <p>
          Resultado: <span className="font-medium">{motor.final_result}</span>, em{' '}
          {formatDateTime(motor.created_at)}.
          {motor.simulated ? ' Execucao com dado ficticio (SIMULADO).' : ''}
        </p>
        {motor.pendencies.length > 0 ? (
          <ul className="list-disc pl-5 text-xs text-ink-600">
            {motor.pendencies.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
      </CardBody>
    </Card>
  );
}

function HistoryCard({ quadro }: { quadro: OperationAuthority }) {
  return (
    <Card>
      <CardHeader
        title="Historico dos pareceres"
        description="Cada decisao fica gravada como aconteceu, e nenhuma linha muda."
      />
      <CardBody>
        {quadro.history.length === 0 ? (
          <p className="text-sm text-ink-600">Nenhuma decisao registrada ainda.</p>
        ) : (
          <ol className="flex flex-col gap-3">
            {quadro.history.map((h) => (
              <li
                key={h.id}
                className="rounded-md border border-line px-3 py-2 text-sm"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink-900">
                    {areaLabels[h.area]}: {eventLabels[h.event]}
                  </span>
                  <span className="text-2xs text-ink-500">
                    {h.actor_label} em {formatDateTime(h.created_at)}, etapa {h.stage}
                  </span>
                  {h.against_engine ? (
                    <Badge tone="warning">Contra a recomendacao do motor</Badge>
                  ) : null}
                </div>
                {h.reason ? <p className="text-ink-700">Motivo: {h.reason}</p> : null}
                {h.justification ? (
                  <p className="text-ink-700">Justificativa: {h.justification}</p>
                ) : null}
                <p className="text-2xs text-ink-500">
                  {h.authority.applied
                    ? `Alcada: ${h.authority.level_label ?? 'sem nivel'}${h.value ? `, valor ${formatMoney(h.value)}` : ''}.`
                    : (h.authority.note ?? '')}
                </p>
              </li>
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}
